import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { GiFairyWings } from 'react-icons/gi';
import { Helmet } from "react-helmet-async";
import axiosInstance from '../../api/axiosInstance';
import './ViewDonorNidhi.css';

const EF_TYPE = 'Encouragement Fund (प्रोत्साहन निधि)';
const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

const ViewDonorNidhi = () => {
  const [donors, setDonors] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => { fetchDonors(); }, []);

  const fetchDonors = async () => {
    try {
      const res = await axiosInstance.get('/api/donors');
      setDonors(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (error) { console.error("Failed to fetch donors", error); }
  };

 const allDonationRows = useMemo(() => {
    let rows = [];
    donors.forEach(donor => {
      if (donor.donations && donor.donations.length > 0) {
        donor.donations.forEach(donation => {
          
          // STRICT SECURITY CHECK: Only show successful and active Nidhi donations
          if (donation.donationType === EF_TYPE && donation.txnStatus === "Success" && donation.isActive !== false) {
            rows.push({
              ...donation,
              donorId: donor._id, 
              memberId: donor.memberId, 
              name: donor.name,
              fatherName: donor.fatherName,
              currentAddress: donor.currentAddress,
              permanentAddress: donor.permanentAddress,
              state: donor.state, 
              district: donor.district
            });
          }

        });
      }
    });
    return rows.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [donors]);

  const availableYears = useMemo(() => {
    const years = allDonationRows.map(r => new Date(r.date).getFullYear().toString());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [allDonationRows]);

  const filteredData = useMemo(() => {
    let list = allDonationRows;
    if (yearFilter) list = list.filter(r => new Date(r.date).getFullYear().toString() === yearFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(r => (r.name && r.name.toLowerCase().includes(q)) || (r.memberId && r.memberId.toLowerCase().includes(q)));
    }
    return list;
  }, [allDonationRows, searchQuery, yearFilter]);

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / limit) || 1;
  const startRecord = (currentPage - 1) * limit + 1;
  const endRecord = Math.min(currentPage * limit, totalItems);

  useEffect(() => { if (currentPage > totalPages) setCurrentPage(1); }, [totalPages, currentPage]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return filteredData.slice(start, start + limit);
  }, [filteredData, currentPage, limit]);

  const totalAmount = useMemo(() => {
    return filteredData.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  }, [filteredData]);

  const handlePageChange = (newPage) => { if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage); };
  const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);

  return (
    <>
      <Helmet>
        <title>Nidhi Donor List | Rajput Shiksha Kosh</title>
        <meta
          name="description"
          content="Protsahan Nidhi (Encouragement Fund) donor list for Rajput Shiksha Kosh, showcasing generous contributions supporting education and scholarships for underprivileged students."
        />
      </Helmet>
    <section className="view-donor-protsahan-nidhi-page-wrapper">
      <div className="view-donor-protsahan-nidhi-page-container">
        
        <div className="view-donor-protsahan-nidhi-page-header">
          <h2 className="view-donor-protsahan-nidhi-page-title">प्रोत्साहन निधि दाता सूची</h2>
          <div className="view-donor-protsahan-nidhi-page-divider">
            <span className="view-donor-protsahan-nidhi-page-divider-line"></span>
            <GiFairyWings className="view-donor-protsahan-nidhi-page-divider-icon" />
            <span className="view-donor-protsahan-nidhi-page-divider-line"></span>
          </div>
        </div>

        <div className="view-donor-protsahan-nidhi-page-top-bar">
          <div className="view-donor-protsahan-nidhi-page-size">
            <label>Show</label>
            <select className="view-donor-protsahan-nidhi-page-filter-select" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }}>
              {PAGE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <label>entries</label>
          </div>

          <div className="view-donor-protsahan-nidhi-page-filters">
            <div className="view-donor-protsahan-nidhi-page-search-box">
              <FaSearch className="view-donor-protsahan-nidhi-page-icon-search" />
              <input type="text" placeholder="Search ID, Name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>

            <select className="view-donor-protsahan-nidhi-page-filter-select" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
              <option value="">Filter by Year (All)</option>
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <div className="view-donor-protsahan-nidhi-page-table-main-container">
          <div className="view-donor-protsahan-nidhi-page-table-responsive">
            <table className="view-donor-protsahan-nidhi-page-table">
              <thead>
                <tr>
                  <th className="view-donor-protsahan-nidhi-page-col-sno">S.No</th>
                  <th className="view-donor-protsahan-nidhi-page-col-date">Date</th>
                  <th className="view-donor-protsahan-nidhi-page-col-memid">Member ID</th>
                  <th className="view-donor-protsahan-nidhi-page-col-name view-donor-protsahan-nidhi-page-align-left">Donor Name</th>
                  <th className="view-donor-protsahan-nidhi-page-col-father view-donor-protsahan-nidhi-page-align-left">Father's Name</th>
                  <th className="view-donor-protsahan-nidhi-page-col-type">Type</th>
                  <th className="view-donor-protsahan-nidhi-page-col-amount view-donor-protsahan-nidhi-page-align-right">Credit (₹)</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr><td colSpan="7" className="view-donor-protsahan-nidhi-page-no-data">No donations match your search criteria.</td></tr>
                ) : paginatedData.map((row, i) => (
                  <React.Fragment key={row._id}>
                    <tr className="view-donor-protsahan-nidhi-page-table-row">
                      <td className="view-donor-protsahan-nidhi-page-col-sno">{startRecord + i}</td>
                      <td className="view-donor-protsahan-nidhi-page-col-date">{new Date(row.date).toLocaleDateString('en-IN')}</td>
                      <td className="view-donor-protsahan-nidhi-page-col-memid font-weight-bold text-primary">{row.memberId || '—'}</td>
                      <td className="view-donor-protsahan-nidhi-page-col-name view-donor-protsahan-nidhi-page-align-left">
                        <div className="view-donor-protsahan-nidhi-page-name-wrapper" onClick={() => toggleRow(row._id)}>
                          <span className="font-weight-bold">{row.name}</span>
                          <button className="view-donor-protsahan-nidhi-page-expand-trigger">{expandedRow === row._id ? <FaChevronUp /> : <FaChevronDown />}</button>
                        </div>
                      </td>
                      <td className="view-donor-protsahan-nidhi-page-col-father view-donor-protsahan-nidhi-page-align-left">{row.fatherName || '—'}</td>
                      <td className="view-donor-protsahan-nidhi-page-col-type">प्रोत्साहन निधि</td>
                      <td className="view-donor-protsahan-nidhi-page-col-amount view-donor-protsahan-nidhi-page-align-right font-weight-bold" style={{color: '#16a085'}}>₹{Number(row.amount).toLocaleString('en-IN')}</td>
                    </tr>
                    {expandedRow === row._id && (
                      <tr className="view-donor-protsahan-nidhi-page-expanded-row">
                        <td colSpan="7">
                          <div className="view-donor-protsahan-nidhi-page-expanded-content">
                            <div className="view-donor-protsahan-nidhi-page-expanded-grid">
                              <div className="view-donor-protsahan-nidhi-page-expanded-detail exp-memid"><span className="view-donor-protsahan-nidhi-page-expanded-label">Member ID</span><span className="view-donor-protsahan-nidhi-page-expanded-value text-primary">{row.memberId || '—'}</span></div>
                              <div className="view-donor-protsahan-nidhi-page-expanded-detail exp-date"><span className="view-donor-protsahan-nidhi-page-expanded-label">Date</span><span className="view-donor-protsahan-nidhi-page-expanded-value">{new Date(row.date).toLocaleDateString('en-IN')}</span></div>
                              <div className="view-donor-protsahan-nidhi-page-expanded-detail exp-father"><span className="view-donor-protsahan-nidhi-page-expanded-label">Father's Name</span><span className="view-donor-protsahan-nidhi-page-expanded-value">{row.fatherName || '—'}</span></div>
                              <div className="view-donor-protsahan-nidhi-page-expanded-detail exp-type"><span className="view-donor-protsahan-nidhi-page-expanded-label">Donation Type</span><span className="view-donor-protsahan-nidhi-page-expanded-value">प्रोत्साहन निधि</span></div>
                              <div className="view-donor-protsahan-nidhi-page-expanded-detail exp-amount"><span className="view-donor-protsahan-nidhi-page-expanded-label">Donation Credit</span><span className="view-donor-protsahan-nidhi-page-expanded-value" style={{color: '#16a085', fontWeight: 'bold'}}>₹{Number(row.amount).toLocaleString('en-IN')}</span></div>
                              
                              <div className="view-donor-protsahan-nidhi-page-expanded-detail"><span className="view-donor-protsahan-nidhi-page-expanded-label">State</span><span className="view-donor-protsahan-nidhi-page-expanded-value">{row.state || '—'}</span></div>
                              <div className="view-donor-protsahan-nidhi-page-expanded-detail"><span className="view-donor-protsahan-nidhi-page-expanded-label">City / District</span><span className="view-donor-protsahan-nidhi-page-expanded-value">{row.district || '—'}</span></div>
                              <div className="view-donor-protsahan-nidhi-page-expanded-detail exp-full-width"><span className="view-donor-protsahan-nidhi-page-expanded-label">Current Address</span><span className="view-donor-protsahan-nidhi-page-expanded-value">{row.currentAddress || '—'}</span></div>
                              <div className="view-donor-protsahan-nidhi-page-expanded-detail exp-full-width"><span className="view-donor-protsahan-nidhi-page-expanded-label">Permanent Address</span><span className="view-donor-protsahan-nidhi-page-expanded-value">{row.permanentAddress || '—'}</span></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="view-donor-protsahan-nidhi-page-table-footer-row">
                  <td colSpan="7" className="view-donor-protsahan-nidhi-page-total-cell">
                    <div className="view-donor-protsahan-nidhi-page-total-container">
                      <span className="view-donor-protsahan-nidhi-page-total-label">{yearFilter ? `Total Donation in ${yearFilter}:` : 'Total Donation (All Years):'}</span>
                      <span className="view-donor-protsahan-nidhi-page-total-value">₹{totalAmount.toLocaleString('en-IN')}</span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="view-donor-protsahan-nidhi-page-bottom-bar">
          <div className="view-donor-protsahan-nidhi-page-pagination-info">Showing {totalItems === 0 ? 0 : startRecord} to {endRecord} of {totalItems} entries</div>
          <div className="view-donor-protsahan-nidhi-page-pagination-controls">
            <button className="view-donor-protsahan-nidhi-page-page-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
            <button className="view-donor-protsahan-nidhi-page-page-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
          </div>
        </div>

      </div>
    </section>
    </>
  );
};

export default ViewDonorNidhi;